/**
 * Follow-Up Task Section
 *
 * Two-mode selector: add a flexible activity from the patient's care plan,
 * or create a custom standalone task. Phase 3 of standalone task deprecation.
 */

// Components
import { Alert, Group, Select, Stack, Text, Textarea } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";

// Constants
import { CUSTOM_TASK_TYPE } from "@benchmark/core/constants";

// Hooks
import { usePatientFlexibleActivities } from "@/hooks/domain/usePatientFlexibleActivities";

// Types
import type { UseFormReturnType } from "@mantine/form";
import type { PractitionerRole } from "@medplum/fhirtypes";

// Utils
import { useMemo } from "react";
import { getDateOnly } from "@benchmark/ui/utils";
import { IconX } from "@tabler/icons-react";

const FOLLOWUP_MODE_OPTIONS = [
  { value: "", label: "None" },
  { value: "plan-activity", label: "Add Plan Activity" },
  { value: "custom", label: "Create Custom Task" },
] as const;

export type FollowUpTaskSectionProps = {
  form: UseFormReturnType<{
    "followup-mode"?: "plan-activity" | "custom";
    "followup-activity-code"?: string;
    "followup-care-plan-id"?: string;
    "followup-task-type"?: string;
    "followup-due-date"?: string;
    "followup-description"?: string;
    "followup-assigned-to"?: string;
    "followup-assigned-to-display"?: string;
    [key: string]: unknown;
  }>;
  patientId?: string;
  practitionerRoleOptions: PractitionerRole[];
};

/**
 * Form section for follow-up task (type, description, owner, due date).
 * @agentic connections
 */
export const FollowUpTaskSection = ({
  form,
  patientId,
  practitionerRoleOptions,
}: FollowUpTaskSectionProps) => {
  const { carePlan, flexibleActivityOptions, isLoading, hasActiveCarePlan } =
    usePatientFlexibleActivities(patientId);

  const followupOwnerOptions = useMemo(() => {
    const roles = practitionerRoleOptions || [];
    return roles
      .filter((r) => !!r.practitioner?.display)
      .slice()
      .sort((a, b) =>
        (a.practitioner?.display || "").localeCompare(
          b.practitioner?.display || "",
        ),
      )
      .map((role) => ({
        value: role.id || "",
        label: role.practitioner?.display || "",
      }));
  }, [practitionerRoleOptions]);

  const modeOptions = useMemo(() => {
    const options = [...FOLLOWUP_MODE_OPTIONS];
    if (!hasActiveCarePlan && patientId) {
      return options.map((opt) =>
        opt.value === "plan-activity" ? { ...opt, disabled: true } : opt,
      );
    }
    return options;
  }, [hasActiveCarePlan, patientId]);

  const planActivitySelectData = useMemo(
    () =>
      flexibleActivityOptions.map((a) => ({
        value: a.value,
        label: a.label,
        description: a.description,
      })),
    [flexibleActivityOptions],
  );

  const handleModeChange = (value: string | null) => {
    const mode = value === "plan-activity" || value === "custom" ? value : "";
    form.setFieldValue("followup-mode", mode || undefined);
    if (mode === "plan-activity") {
      form.setFieldValue("followup-task-type", "");
      form.setFieldValue("followup-description", "");
      form.setFieldValue("followup-assigned-to", "");
      form.setFieldValue("followup-assigned-to-display", "");
      form.setFieldValue("followup-due-date", "");
    } else if (mode === "custom") {
      form.setFieldValue("followup-task-type", CUSTOM_TASK_TYPE.code);
      form.setFieldValue("followup-activity-code", "");
      form.setFieldValue("followup-care-plan-id", "");
    } else {
      form.setFieldValue("followup-activity-code", "");
      form.setFieldValue("followup-care-plan-id", "");
      form.setFieldValue("followup-task-type", "");
      form.setFieldValue("followup-description", "");
      form.setFieldValue("followup-assigned-to", "");
      form.setFieldValue("followup-assigned-to-display", "");
      form.setFieldValue("followup-due-date", "");
    }
  };

  const handlePlanActivityChange = (value: string | null) => {
    const code = value || "";
    form.setFieldValue("followup-activity-code", code);
    form.setFieldValue("followup-care-plan-id", carePlan?.id || "");
  };

  const followupMode = form.values["followup-mode"];
  const isPlanActivityMode = followupMode === "plan-activity";
  const isCustomMode = followupMode === "custom";

  return (
    <>
      <FormField
        label="Follow-Up Task"
        variant="section"
        showDivider={false}
        description="Add a plan activity or create a custom task (optional)"
      >
        <Select
          value={followupMode || ""}
          onChange={handleModeChange}
          data={modeOptions}
          searchable={false}
          clearable
          disabled={isLoading}
        />
      </FormField>

      {isPlanActivityMode && (
        <Stack gap="sm">
          {!hasActiveCarePlan && patientId && (
            <Alert
              icon={<IconInfoCircle size={16} />}
              color="blue"
              variant="light"
              title="No active care plan"
            >
              <Text size="sm">
                Enroll the member in a care plan (intake, triage, or CoCM) to
                add plan activities. Use &quot;Create Custom Task&quot; for
                ad-hoc follow-up.
              </Text>
            </Alert>
          )}

          {hasActiveCarePlan && (
            <>
              <FormField label="Activity" required>
                <Select
                  value={form.values["followup-activity-code"] || ""}
                  onChange={handlePlanActivityChange}
                  data={planActivitySelectData}
                  searchable
                  clearable
                  placeholder="Select activity"
                />
              </FormField>

              {form.values["followup-activity-code"] && (
                <Alert
                  icon={<IconInfoCircle size={16} />}
                  color="purple"
                  variant="light"
                >
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      {
                        planActivitySelectData.find(
                          (a) =>
                            a.value === form.values["followup-activity-code"],
                        )?.description
                      }
                    </Text>
                  </Group>
                </Alert>
              )}
            </>
          )}
        </Stack>
      )}

      {isCustomMode && (
        <Stack gap="sm">
          <FormField label="Description" required>
            <Textarea
              value={form.values["followup-description"] || ""}
              onChange={(e) =>
                form.setFieldValue("followup-description", e.target.value)
              }
              placeholder="Describe what needs to happen next..."
              autosize
              minRows={2}
            />
          </FormField>

          <FormGroup>
            <FormField label="Owner" required>
              <Select
                placeholder="Assign to practitioner"
                value={form.values["followup-assigned-to"] || ""}
                onChange={(value, option) => {
                  form.setFieldValue("followup-assigned-to", value || "");
                  const optLabel =
                    (option as { label?: string } | null)?.label ||
                    followupOwnerOptions.find((o) => o.value === value)
                      ?.label ||
                    "";
                  form.setFieldValue("followup-assigned-to-display", optLabel);
                }}
                data={followupOwnerOptions}
                searchable
                clearable
              />
            </FormField>

            <FormField label="Due Date">
              <TextInput
                type="date"
                value={
                  form.values["followup-due-date"]
                    ? (() => {
                        const dateValue = form.values[
                          "followup-due-date"
                        ] as string;
                        if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                          return dateValue;
                        }
                        return getDateOnly(dateValue)
                          .toISOString()
                          .split("T")[0];
                      })()
                    : ""
                }
                onChange={(e) =>
                  form.setFieldValue(
                    "followup-due-date",
                    e.currentTarget.value || "",
                  )
                }
                rightSection={
                  form.values["followup-due-date"] ? (
                    <IconX
                      size={16}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        form.setFieldValue("followup-due-date", "")
                      }
                    />
                  ) : undefined
                }
              />
            </FormField>
          </FormGroup>
        </Stack>
      )}
    </>
  );
};
